import React, { useEffect, useMemo, useState } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { ZOHO_STATE_KEY } from './LoginPage';
import type { LoginResponse } from '../services/api/types/common';

function readOAuthParams(search: string): { code: string; state: string } {
  const params = new URLSearchParams(search);
  return {
    code: (params.get('code') || '').trim(),
    state: (params.get('state') || '').trim(),
  };
}

/** Survives React Strict Mode remounts so the one-time Zoho code is not double-redeemed. */
const zohoOAuthInflight = new Map<string, Promise<LoginResponse>>();

export function ZohoOAuthCallbackPage() {
  const { completeLoginFromResponse, isAuthenticated, loading } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { code, state } = useMemo(() => readOAuthParams(location.search), [location.search]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || isAuthenticated) return;
    if (!code || !state) {
      setError('Missing Zoho authorization response. Try signing in again.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        let pending = zohoOAuthInflight.get(state);
        if (!pending) {
          const expectedState = sessionStorage.getItem(ZOHO_STATE_KEY) || '';
          if (!expectedState || expectedState !== state) {
            setError('Zoho sign-in state mismatch. Try again from the login page.');
            return;
          }
          sessionStorage.removeItem(ZOHO_STATE_KEY);
          pending = apiService.completeZohoOAuthLogin({ code, state }).then((res) => res.data);
          zohoOAuthInflight.set(state, pending);
        }

        const data = await pending;
        await completeLoginFromResponse(data);
        zohoOAuthInflight.delete(state);
        if (!cancelled) {
          history.replace('/');
        }
      } catch (err) {
        zohoOAuthInflight.delete(state);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Zoho sign-in failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, state, completeLoginFromResponse, history, isAuthenticated, loading]);

  if (!loading && isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div className="min-h-screen royal-loading flex items-center justify-center p-6">
      <div className="royal-card p-8 max-w-md text-center border border-primary-500/20">
        {error ? (
          <>
            <p className="text-sm text-red-600" role="alert">{error}</p>
            <Link to="/login" className="text-primary-600 font-medium text-sm mt-4 inline-block">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="royal-loading-spinner mx-auto mb-4" aria-hidden />
            <p className="text-sm text-secondary-700">Completing Zoho sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
